import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isDesktop } from '@westwing/ui-kit/breakPoints';
import { StateInterface } from '../redux/types';
import { showOverlay } from '../redux/overlays/actions';
import { getOverlayById } from '../redux/overlays/selectors';
import { cookiesEnabled } from '../../common/helpers/environment';
import { ShopToClubConfigState } from '../../common/types/remoteConfig';
import useConfig from './useConfig';
import useDeviceType from './useDeviceType';
import useUserTracking from './flags/useUserTracking';
import useWasClubRegistrationViewed from './flags/useWasClubRegistrationViewed';
import useNeverShowShopToClub from './flags/useNeverShowShopToClub';
import useListingPageUrlState from './ListingPage/useListingPageUrlState';

export const CLUB_REGISTRATION_COOKIE = 'was_club_registration_overlay_shown';

export enum PAGE_VIEW {
    firstPageView = 'first_page_view',
    secondPageView = 'second_page_view',
}

export interface ReduxStateProps {
    data: ShopToClubConfigState;
    isVisible: boolean;
}

const useClubRegistrationPopup = () => {
    const [hasBeenViewed, setHasBeenViewedStatus] = useWasClubRegistrationViewed();
    const dispatch = useDispatch();
    const [{ listingPageKey: currentPageKey }] = useListingPageUrlState();
    const { isReturningUser, isLoggedIn, didLoginFinishOnce, isRegistered } = useUserTracking();
    const { isApp } = useDeviceType();
    const config = useConfig();
    const [neverShowShopToClub] = useNeverShowShopToClub();
    const [lastPageShown, setLastPageShown] = useState<string | undefined>();
    const pageInvalidated = !lastPageShown || currentPageKey !== lastPageShown;

    const isVisible = useSelector<StateInterface, boolean>(
        state => getOverlayById(state.overlays, config.shopToClubOverlayId).isActive
    );
    const data = useSelector<StateInterface, ShopToClubConfigState>(state => {
        return state.configurations.shopToClub;
    });

    const isDisabledFromBob = data && !data.isEnabled;
    let canShowOnThisPage = isReturningUser && (isVisible || !hasBeenViewed);

    if (isDesktop() && data.pageViewLimit === PAGE_VIEW.firstPageView) {
        canShowOnThisPage = isVisible || !hasBeenViewed;
    }

    // We need to be sure we have requested login check, else we may show to logged in users
    const failConditions = [
        isRegistered,
        !didLoginFinishOnce,
        isApp,
        isLoggedIn,
        !data,
        isDisabledFromBob,
        !canShowOnThisPage,
        neverShowShopToClub,
        !pageInvalidated,
    ];
    const skipRender = failConditions.find(condition => !!condition);

    useEffect(() => {
        const expirationInDays = data && data.expirationInDays;
        const cookieTTL = expirationInDays * 24 * 60 * 60 * 1000;

        const wasExpirationDisabled = !expirationInDays && hasBeenViewed;
        if (wasExpirationDisabled && cookiesEnabled) {
            setHasBeenViewedStatus(0, cookieTTL);
        }

        if (data && !skipRender && !isVisible && cookiesEnabled) {
            const mobileDelayMS = data.mobileDelayInSeconds * 1000;

            if (cookieTTL) {
                setHasBeenViewedStatus(1, cookieTTL);
            }

            setTimeout(() => {
                setLastPageShown(currentPageKey);
                dispatch(showOverlay(config.shopToClubOverlayId));
            }, mobileDelayMS);
        }
    }, [
        data,
        skipRender,
        setHasBeenViewedStatus,
        isVisible,
        dispatch,
        config.shopToClubOverlayId,
        hasBeenViewed,
        currentPageKey,
    ]);

    return !skipRender;
};

export default useClubRegistrationPopup;
